import React from "react";
import styled from "styled-components";
import Img from "../../Img";
import { H3 as BaseH3, Label as BaseLabel, Text as BaseText, useWindowSize } from "@commonsku/styles";
import { usePublicViewEntityContext } from "../../../context/PublicViewEntityProvider";
import { formatName } from "../../client-page/utils";
import { mobileScreenSize } from "../../../utils";

const FooterContainer = styled.div`
    &&& {
        display: flex;
        align-items: center;
        height: 128px;
        width: 100%;
        margin-bottom: 0;
        margin-top: auto;
        padding: 16px;
        background: var(--color-neutrals-20);
        gap: 16px;
        justify-content: space-between;
        white-space: nowrap;

        @media (max-width: ${mobileScreenSize}px) {
            flex-direction: column;
            height: auto;
            padding: 8px;
            align-items: flex-start;
        }
    }
`;

const LogoContainer = styled.div`
    display: flex;
    height: 80px;
    gap: 16px;
`;

const PresentationInfo = styled.div`
    display: flex;
    flex: 1 1 50%;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: ${mobileScreenSize}px) {
        flex-basis: 100%;
    }
`;

const ContactInfo = styled.div`
    display: flex;
    flex: 1 1 50%;
    justify-content: flex-end;
    gap: 24px;
    overflow: hidden;

    @media (max-width: ${mobileScreenSize}px) {
        flex-basis: 100%;
        flex-direction: column;
        gap: 8px;
    }
`;

const ContactInfoColumn = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const H3 = styled(BaseH3)`
    &&& {
        margin-bottom: 0;
        line-height: 32px;
        font-size: 20px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const Label = styled(BaseLabel)`
    &&& {
        font-size: 16px;
        font-weight: 500;
        color: var(--color-neutrals-90);
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const Text = styled(BaseText)`
    &&& {
        font-family: var(--skufont-regular);
        font-size: 14px;
        font-weight: 400;
        color: var(--color-neutrals-90);
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

interface FooterProps {
    isEditable?: boolean;
}

const Footer = ({ isEditable = false }: FooterProps) => {
    const {
      entityNumber: presentationNumber,
      entity = {} as any,
      title: presentationName,
    } = usePublicViewEntityContext();
    const [windowWidth] = useWindowSize();
    const isMobile = windowWidth <= mobileScreenSize;

    if (isEditable && isMobile) {
        return null;
    }

    const companyAvatar = entity.company?.company_avatar ||entity.company_data?.company_avatar?.original || '';
    const companyName = entity.company?.company_name || '';
    const clientName = entity.client_name || '';
    const orderTemplate = entity.order_template || '';
    const rep = entity.resku_form && orderTemplate && 'footer_user_type' in orderTemplate && orderTemplate.footer_user_type === 'CLIENT-REP'
        ? entity.client_rep
        : entity.sales_rep;
    const repName = formatName(rep?.contact_first_name, rep?.contact_last_name);
    const repEmail = rep?.contact_email || '';
    const address = (entity.resku_form && entity.order_template?.address
        ? entity.order_template?.address
        : entity.company_address) ?? {};
    const addressCityStatePostal = (address.address_city ? `${address.address_city}, ` : '')
        + (address.address_state ? `${address.address_state} ` : '')
        + (address.address_postal || '');

    return (
        <FooterContainer>
            <LogoContainer>
                {companyAvatar &&
                    <Img src={companyAvatar} alt={companyName} width={80} height={80} />
                }
                <PresentationInfo>
                    {companyName &&
                        <H3>{companyName}</H3>
                    }
                    <Text>Presentation #{presentationNumber}{clientName ? ` for ${clientName}` : ''}</Text>
                    {presentationName &&
                        <Text>{presentationName}</Text>
                    }
                </PresentationInfo>
            </LogoContainer>
            <ContactInfo>
                <ContactInfoColumn>
                    <Label>{repName}</Label>
                    <Text>{repEmail}</Text>
                </ContactInfoColumn>
                {address &&
                    <ContactInfoColumn>
                        <Label>Billing Address</Label>
                        {address.address_line_1 &&
                            <Text>{address.address_line_1}</Text>
                        }
                        {(address.address_city || address.address_state || address.address_postal) &&
                            <Text>{addressCityStatePostal}</Text>
                        }
                        {address.address_country &&
                            <Text>{address.address_country}</Text>
                        }
                    </ContactInfoColumn>
                }
            </ContactInfo>
        </FooterContainer>
    );
}

export default Footer;
